import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Star, ChevronDown, ChevronUp } from "lucide-react";
import * as Icons from "lucide-react";
import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CTA from "@/components/sections/CTA";
import { PRICING_TIERS, ADD_ONS, MANAGED, FAQ_ITEMS } from "@/lib/content";

const accentText: Record<string, string> = {
  cyan: "text-edx-cyan", purple: "text-edx-purple", pink: "text-edx-pink", orange: "text-edx-orange", blue: "text-edx-blue",
};

export default function Pricing({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [faqOpen, setFaqOpen] = useState<number | null>(0);
  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="pt-28 pb-12 relative page-fade" data-testid="pricing-page">
        <div className="absolute inset-0 edx-aurora-bg opacity-30" />
        <div className="relative">
          <section className="container-edx text-center max-w-3xl mx-auto pt-16 pb-16 md:pb-24">
            <div className="section-index mx-auto">EDX.01</div>
            <h1 className="display mt-4 text-4xl md:text-6xl text-white">
              Pricing that <span className="gradient-text">scales with you</span>.
            </h1>
            <p className="mt-5 text-edx-muted">All prices in South African Rand (ZAR). Final quotes confirmed after free consultation.</p>
          </section>

          {/* Tiers */}
          <section className="container-edx pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {PRICING_TIERS.map((t, i) => (
              <motion.div
                key={t.key} data-testid={`tier-${t.key}`}
                initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`relative glass glass-edge tilt p-6 flex flex-col ${t.popular ? "lg:scale-[1.04] ring-2 ring-edx-pink/40" : ""}`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 btn btn-primary !py-1 !px-3 !text-[10px] uppercase tracking-widest">
                    <Sparkles size={10} /> Most Popular
                  </div>
                )}
                <div className={`text-[10px] uppercase tracking-widest font-mono font-semibold ${accentText[t.accent]}`}>{t.cadence}</div>
                <div className="mt-1 display font-extrabold text-4xl text-white">{t.price}</div>
                <div className="mt-3 font-display font-semibold text-lg text-white">{t.name}</div>
                <p className="mt-1 text-xs text-edx-muted leading-relaxed">{t.tagline}</p>
                <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent my-5" />
                <ul className="space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-edx-muted">
                      <Check size={13} className={`${accentText[t.accent]} mt-0.5 shrink-0`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 text-[11px] text-edx-muted italic">{t.extra}</div>
                <Link to={`/book?tier=${t.key}`} data-testid={`tier-cta-${t.key}`}
                  className="btn btn-ghost mt-5 w-full !text-sm">
                  Get started <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </section>

          {/* Add-ons */}
          <section className="pricing-section">
          <div className="container-edx">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-4">
                <div className="section-index">EDX.02</div>
                <h2 className="display mt-4 text-3xl md:text-4xl text-white">
                  Everything else, <span className="gradient-text">handled</span>.
                </h2>
                <p className="mt-4 text-edx-muted text-sm leading-relaxed">
                  Company registration, domains, branding, copywriting — bolt-on the pieces you need.
                </p>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ADD_ONS.map((a, i) => {
                  const Icon = (Icons as any)[a.icon] || Icons.Plus;
                  return (
                    <motion.div key={a.name} data-testid={`addon-${i}`}
                      initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.04 }}
                      className="glass p-5 flex items-center gap-4 group hover:border-edx-cyan/40 transition-all">
                      <div className="h-10 w-10 rounded-lg bg-edx-cyan/10 flex items-center justify-center text-edx-cyan group-hover:bg-edx-purple/15 group-hover:text-edx-purple transition-colors shrink-0">
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm text-white truncate">{a.name}</div>
                        <div className="text-[11px] text-edx-muted">{a.note}</div>
                      </div>
                      <div className="font-display font-bold text-sm gradient-text shrink-0">{a.price}</div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
          </section>

          {/* Managed */}
          <section className="pricing-section">
          <div className="container-edx">
            <div className="text-center max-w-2xl mx-auto">
              <div className="section-index mx-auto">EDX.03</div>
              <h2 className="display mt-4 text-3xl md:text-5xl text-white">
                Stay live. Stay <span className="gradient-text">ahead</span>.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {MANAGED.map((m, i) => (
                <motion.div key={m.name} data-testid={`managed-${m.name.toLowerCase()}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`glass glass-edge tilt p-7 relative ${m.popular ? "ring-2 ring-edx-purple/40 md:scale-[1.04]" : ""}`}>
                  {m.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 btn btn-primary !py-1 !px-3 !text-[10px] uppercase tracking-widest">
                      <Star size={10} /> Recommended
                    </div>
                  )}
                  <div className="font-display font-semibold text-edx-muted text-xs uppercase tracking-widest font-mono">{m.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="display font-extrabold text-4xl gradient-text">{m.price}</span>
                    <span className="text-edx-muted text-sm">{m.cadence}</span>
                  </div>
                  <ul className="mt-6 space-y-2.5">
                    {m.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-edx-muted">
                        <Check size={13} className={accentText[m.accent]} /> {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
          </section>

          {/* FAQ */}
          <section className="pricing-section">
          <div className="container-edx">
            <div className="text-center max-w-2xl mx-auto">
              <div className="section-index mx-auto">EDX.04</div>
              <h2 className="display mt-4 text-3xl md:text-5xl text-white">Common questions, <span className="gradient-text">straight answers</span>.</h2>
            </div>
            <div className="mt-12 max-w-3xl mx-auto space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <div key={i} className="glass p-5 cursor-pointer" data-testid={`faq-${i}`}
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-display font-semibold text-white text-sm">{f.q}</h3>
                    {faqOpen === i ? <ChevronUp size={16} className="text-edx-cyan" /> : <ChevronDown size={16} className="text-edx-muted" />}
                  </div>
                  {faqOpen === i && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                      className="mt-3 text-sm text-edx-muted leading-relaxed">{f.a}</motion.p>
                  )}
                </div>
              ))}
            </div>
          </div>
          </section>

          <CTA index="05" />
        </div>
      </main>
      <Footer />
    </>
  );
}
